import { useCallback, useEffect, useState } from 'react';
import {
  requests,
  type Category,
  type RequestStatus,
  type SiteRequest,
} from '../lib/api.ts';
import { StatusPill } from './StatusPill.tsx';
import { assignRequest, assignable, uploadAttachment }
  from '../lib/api.ts';
import { REASON } from './AttachButton.tsx';
import { Modal } from './Modal';
import { PAGE, Pager } from './Pager';
import { Thread } from './Thread.tsx';

interface Props {
  /** Devices file; accounts triage. Drives which controls appear. */
  readonly canFile: boolean;
  /** Whose tickets are mine, for the ownership rule. */
  readonly meId?: string | null;
  readonly canTriage: boolean;
  readonly categories: readonly Category[];
  /** Site chosen in the header. Requests must follow it, or an admin
      sees another site's tickets under this site's name. */
  readonly siteId: string;
  /** Arriving from a search hit: open this one expanded. */
  readonly focusRequest?: string;
}

const NEXT_ACTIONS: Partial<Record<RequestStatus, RequestStatus[]>> = {
  open: ['acknowledged', 'in_progress', 'resolved'],
  acknowledged: ['in_progress', 'resolved'],
  in_progress: ['resolved'],
  resolved: ['in_progress', 'closed'],
  closed: ['open'],
};

const actionLabel = (from: RequestStatus, to: RequestStatus): string =>
  to === 'in_progress' && (from === 'resolved' || from === 'closed')
    ? 'Reopen'
    : ACTION_LABEL[to];

const ACTION_LABEL: Record<RequestStatus, string> = {
  open: 'Reopen',
  acknowledged: 'Acknowledge',
  in_progress: 'Start work',
  resolved: 'Mark resolved',
  closed: 'Close',
};


/**
 * What the button says depends on who is reading it.
 *
 * A site wants to know whether head office has answered; a supervisor
 * wants to know whether the site has come back. A single total
 * answers neither question.
 */
const replyLabel = (r: SiteRequest, isSite: boolean): string => {
  const theirs = isSite ? r.from_hq : r.from_site;
  if (theirs > 0) {
    return isSite
      ? `${theirs} from head office`
      : `${theirs} from the site`;
  }
  return r.replies > 0 ? `${r.replies} replies` : 'Reply';
};

/**
 * The states a ticket is actually in, grouped the way someone asks
 * about them: what needs me, what is moving, what is finished.
 */
const STATUS_FILTERS: Array<{
  id: string;
  label: string;
  match?: (s: string) => boolean;
}> = [
  { id: 'live', label: 'Needs work',
    match: (s) => s === 'open' || s === 'acknowledged' },
  { id: 'progress', label: 'In progress',
    match: (s) => s === 'in_progress' },
  { id: 'done', label: 'Finished',
    match: (s) => s === 'resolved' || s === 'closed' },
  { id: 'all', label: 'Everything' },
];

export const Requests = ({
  canFile,
  meId,
  canTriage,
  categories,
  siteId,
  focusRequest,
}: Props) => {
  // Everything is fetched once and filtered here: the counts on
  // the chips have to be true for every group, not just the one
  // currently selected.
  const [all, setList] = useState<SiteRequest[]>([]);
  const [filter, setFilter] = useState('live');

  const chosen = STATUS_FILTERS.find((f) => f.id === filter);
  const list = chosen?.match
    ? all.filter((r) => chosen.match!(r.status))
    : all;
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Chosen before filing, uploaded after: the ticket has to
  // exist before anything can attach to it.
  const [photos, setPhotos] = useState<File[]>([]);
  const [page, setPage] = useState(0);
  const [openThread, setOpenThread] = useState('');

  // A search hit that lands on a collapsed list has not really taken
  // you anywhere. Open the thread and drop the open-only filter so the
  // request is visible even if it is already closed.
  useEffect(() => {
    if (!focusRequest) return;
    setOpenThread(focusRequest);
    setFilter('all');
  }, [focusRequest]);
  // Resolving asks for a note inline. window.prompt returned null on
  // Cancel and the code carried on regardless, so a mis-click closed a
  // request with no record of why.
  const [resolving, setResolving] = useState('');
  const [filerPin, setFilerPin] = useState('');
  const [note, setNote] = useState('');

  const refresh = useCallback(() => {
    requests.list(false, siteId).then(setList)
      .catch(() => setList([]));
  }, [siteId]);

  useEffect(refresh, [refresh]);

  useEffect(() => {
    const filable = categories.filter((c) => c.allows_site_initiated);
    if (!categoryId && filable[0]) setCategoryId(filable[0].id);
  }, [categories, categoryId]);


  const [people, setPeople] = useState<
    Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    // A counter screen never hands work over, so it never asks.
    if (canFile) return;
    assignable().then(setPeople).catch(() => setPeople([]));
  }, [canFile]);

  const handOver = async (id: string, userId: string) => {
    await assignRequest(id, userId || null).catch(() => undefined);
    refresh();
  };

  const file = async () => {
    setBusy(true);
    setError('');
    try {
      const made = await requests.file({
        categoryId,
        title: title.trim(),
        body: body.trim(),
        priority: urgent ? 'urgent' : 'normal',
        ...(filerPin.length >= 4 ? { pin: filerPin } : {}),
      });

      // Photos attach to the opening message, which exists only once
      // the ticket is filed. Same order as a notice: send, then
      // attach what was chosen beforehand.
      for (const f of photos) {
        const up = await uploadAttachment(made.messageId, f);
        if (!up.ok) {
          setError(`${f.name}: ${REASON[up.reason]}`);
          break;
        }
      }
      setPhotos([]);
      setTitle('');
      setBody('');
      setUrgent(false);
      setFilerPin('');
      refresh();
    } catch {
      setError('Could not file that. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const move = async (
    id: string,
    next: RequestStatus,
    note?: string,
  ) => {
    setBusy(true);
    setError('');
    try {
      // Acting on a ticket claims it. With three technicians in one
      // category the queue is shared, so a ticket somebody has
      // started must show whose it is or two people drive to the
      // same site.
      const claims = next === 'acknowledged' || next === 'in_progress';
      await requests.setStatus(id, next, note, claims);
      refresh();
    } catch {
      setError('That status change was not allowed.');
    } finally {
      setBusy(false);
    }
  };

  const filable = categories.filter((c) => c.allows_site_initiated);

  return (
    <div className="noticeWrap">
      {error && <p className="err">{error}</p>}

      {canFile && filable.length > 0 && (
        <section className="composer">
          <p className="mono">Report something</p>
          <input
            value={title}
            placeholder="What is wrong? One line."
            onChange={(e) => setTitle(e.target.value)}
          />
          <textarea
            rows={3}
            value={body}
            placeholder="Any detail that helps - what, where, since when."
            onChange={(e) => setBody(e.target.value)}
            style={{ marginTop: 10 }}
          />
          <div className="noticeRow">
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              aria-label="Category"
            >
              {filable.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <label className="check">
              <input
                type="checkbox"
                checked={urgent}
                onChange={(e) => setUrgent(e.target.checked)}
              />
              Urgent
            </label>
            <input
              className="pinInput"
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={filerPin}
              placeholder="PIN"
              title="Optional: puts your name on this ticket"
              onChange={(e) =>
                setFilerPin(e.target.value.replace(/\D/g, ''))
              }
            />
            <button
              className="primary"
              disabled={busy || !title.trim() || !body.trim()}
              onClick={file}
            >
              {busy ? 'Sending...' : 'Send to HQ'}
            </button>

          {/* A photo of the thing that is wrong says more than a
              line of description. */}
          <label className="filePick">
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={(e) => setPhotos(
                e.target.files ? Array.from(e.target.files) : [])}
            />
            <span>
              {photos.length === 0
                ? 'Add a photo or PDF'
                : photos.map((f) => f.name).join(', ')}
            </span>
          </label>
          </div>
        </section>
      )}

      {/* Filter by the status a ticket actually has. The old pair
          overlapped -- "all including finished" contained the open
          ones too -- so there was no way to see just the finished
          work, and no count to tell you where anything was. */}
      <div className="statusBar">
        {STATUS_FILTERS.map((sf) => {
          const n = sf.match
            ? all.filter((r) => sf.match!(r.status)).length
            : all.length;
          return (
            <button
              key={sf.id}
              className={filter === sf.id ? 'chip on' : 'chip'}
              onClick={() => setFilter(sf.id)}
            >
              {sf.label}
              {n > 0 && <b className="chipCount">{n}</b>}
            </button>
          );
        })}
      </div>

      {list.length === 0 ? (
        <p className="empty">
          {all.length === 0
            ? 'No tickets yet.'
            : 'Nothing in this group.'}
        </p>
      ) : (
        <>
        <ul className="reqList">
          {list.slice(page * PAGE, page * PAGE + PAGE).map((r) => (
            <li
              key={r.id}
              className={r.priority === 'urgent' ? 'req urgent' : 'req'}
              style={{ borderLeftColor: r.color }}
            >
              <div className="reqTop">
                <StatusPill status={r.status} />
                <span className="mono">{r.category}</span>
                {r.priority === 'urgent' && (
                  <span className="pinUrg">Urgent</span>
                )}
              </div>

              {/* Which site raised it, in the top row rather than
                  buried in the meta line: across twelve sites it is
                  the first thing you need, not the last. */}
              {r.site_name && (
                <span className="siteTag">{r.site_name}</span>
              )}

              <p className="reqTitle">{r.title}</p>

              <div className="reqMeta">
                <span className="mono">
                  {r.opened_on ? r.opened_on : ''}
                  {r.opened_by ? ` - ${r.opened_by}` : ''}
                </span>
                {r.assignee && (
                  <span className="ownerTag">{r.assignee}</span>
                )}
              </div>

  
            {/* Handing work over is its own act, so it sits apart
                from the status buttons. Clearing it returns the
                ticket to the shared queue. */}
            <div className="assignRow">
              <span className="mono">Owner</span>
              <select
                value={r.assignee_user_id ?? ''}
                onChange={(e) => void handOver(r.id, e.target.value)}
              >
                <option value="">Nobody yet</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            {r.resolution_note && (
                <p className="reqNote">{r.resolution_note}</p>
              )}

              <button
                className="linkish"
                onClick={() => setOpenThread(r.id)}
              >
                {replyLabel(r, canFile)}
              </button>

              {canTriage && (
                <div className="reqActions">
                  {(NEXT_ACTIONS[r.status] ?? []).map((next) => (
                    <button
                      key={next}
                      disabled={busy}
                      onClick={() =>
                        next === 'resolved'
                          ? (setResolving(r.id), setNote(''))
                          : move(r.id, next)
                      }
                    >
                      {actionLabel(r.status, next)}
                    </button>
                  ))}
                </div>
              )}

              {resolving === r.id && (
                <div className="resolveBox">
                  <p className="mono">What was done?</p>
                  <textarea
                    rows={2}
                    autoFocus
                    value={note}
                    placeholder="The site sees this, so say what changed."
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <div className="noticeRow">
                    <button
                      className="primary"
                      disabled={busy || note.trim().length < 3}
                      onClick={() => {
                        void move(r.id, 'resolved', note.trim());
                        setResolving('');
                      }}
                    >
                      Mark resolved
                    </button>
                    <button onClick={() => setResolving('')}>Cancel</button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
        <Pager page={page} count={list.length} onPage={setPage} />
        </>
      )}

      {openThread && (() => {
        const r = list.find((x) => x.id === openThread);
        if (!r) return null;
        // A counter PC has no printer, and printing belongs to
        // head office where a thread becomes evidence.
        return (
          <Modal
            title={r.title}
            subtitle={`${r.site_name ?? ''} \u00b7 ${r.category}`
              + ` \u00b7 ${r.status.replace('_', ' ')}`}
            onClose={() => setOpenThread('')}
            {...(canFile ? {} : { onPrint: () => window.print() })}
          >
            {r.resolution_note && (
              <p className="reqNote">{r.resolution_note}</p>
            )}
            <Thread
              requestId={r.id}
              canReply={r.status !== 'closed'}
              canSign={canFile}
              onReplied={refresh}
            />
          </Modal>
        );
      })()}
    </div>
  );
};
